import React, { useEffect, useRef } from "react";
import { ComponentProps, FileUrlProvider, MediaGallery, MenuControls, RichText } from "api-sdk-react"
import { ActionButton, InfoBox, ReaderConfig } from "reader-common";
import { ChatStyleConfig } from "../../types";
import { ChatMessageHeader } from "./chatMessageHeader";
import { isDisplayNode, Node } from "api-sdk";

export interface PartnerMessageProps extends ComponentProps {
    node: Node,
    config: ReaderConfig,
    fileUrlProvider: FileUrlProvider,
    styleConfig: ChatStyleConfig,
    menu: MenuControls,
    onHelpClick?: (e: React.MouseEvent) => void, // Called when "Hilfe und Infos" is pressed
    animateEntry?: boolean,
    animateLeave?: boolean,
    autoFocus?: boolean
}

export function PartnerMessage(props: PartnerMessageProps) {

    const node = props.node

    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (ref.current && props.autoFocus) {
            setTimeout(() => ref.current.focus(), 150)
        }
    }, [ ref, props.autoFocus ])

    let partnerMessages: JSX.Element[] = [ ]
    let textMessagesCount = 0 // When there are no text messages, display an empty message to subtly show there's a problem

    const messageContentProps = (idx: number) => ({
        className: "message-content" + (props.animateEntry ? " animated-entry-partner" : ""),
        style: { animationDelay: (300 * idx) + "ms" },
        key: "partner-message:" + idx + ":" + node.id
    })

    const textFields: (keyof Node)[] = [ ]
    const mediaGallery = (node.mediaGallery || []).filter(m => !!m)

    if (node.introduction) {
        textFields.push("introduction")
    }
    if (node.instruction || mediaGallery.length) {
        textFields.push("instruction")
    }
    if (node.hint) {
        textFields.push("hint")
    }

    partnerMessages = partnerMessages.concat(textFields.map((field, idx) => {

        const gallery = (field === "instruction" && mediaGallery.length) ? (
            <MediaGallery
                media={ mediaGallery }
                fileUrlProvider={ props.fileUrlProvider }
                showScrollControls
            />
        ) : null

        const text = node[field] ? (
            <RichText text={ node[field] as string } />
        ) : null

        const contentProps = messageContentProps(idx)

        return (
            <div
                { ...contentProps }
                role="document"
                aria-live={ props.animateEntry ? "polite" : undefined }
                className={ contentProps.className + " " + (gallery ? "media-content" : "") }
                key={ node.id + ":" + field }>
                { text }
                { gallery }
            </div>
        )
    }))

    textMessagesCount += textFields.length
    if (node.description) {
        partnerMessages.push(
            <div key={ node.id + ":action:" + 0 } className="message-actions">
                <ActionButton
                    className="message-action fade"
                    text={ props.config.messages.helpAndInfo }
                    onClick={ e => {
                        props.menu.showMenu({
                            anchor: e.currentTarget,
                            render: () => (
                                <InfoBox
                                    text={ node.description }
                                    onClose={ props.menu.dismissMenu }
                                    config={ props.config }
                                />
                            )
                        })
                    } }
                />
            </div>
        )
    }

    if (!textMessagesCount && isDisplayNode(node) && !node.isEndNode) {
        partnerMessages = [
            <div key="empty-notice" { ...messageContentProps(0) }>&nbsp;&nbsp;&nbsp;</div>
        ].concat(partnerMessages)
    }

    return (
        <div
            key={ "partner-message:" + node.id }
            ref={ ref }
            aria-atomic
            tabIndex={ props.animateEntry ? 0 : undefined }
            className={ "message partner-message " + (props.className || "") + (props.animateLeave ? " animated-leave" : "") }>
            <ChatMessageHeader config={ props.config } styleConfig={ props.styleConfig } target="partner" />
            { partnerMessages }
        </div>
    )

}